import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import {
  useV2ListNotificationsQuery,
  useV2NotificationsMarkAllReadMutation,
  useV2NotificationsUnreadCountQuery,
} from "@generated";
import useMarkNotificationRead from "@common/hooks/useMarkNotificationRead";
import NotificationRow from "@components/NotificationRow";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import "./styles.css";

interface NotificationRowData {
  id: string;
  body: string | undefined;
  link: string | undefined;
  readAt: string | undefined | null;
  createdAt: string | undefined;
}

const NotificationsMspa = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const handleView = useMarkNotificationRead();

  const { data, isLoading, refetch } = useV2ListNotificationsQuery({
    page: { number: pageNumber, size: pageSize },
  });
  const { refetch: refetchUnreadCount } = useV2NotificationsUnreadCountQuery();
  const [markAllRead] = useV2NotificationsMarkAllReadMutation();

  const handleClearAll = async () => {
    await markAllRead().unwrap();
    refetch();
    refetchUnreadCount();
  };

  const notifications: NotificationRowData[] = (data?.data ?? [])
    .filter((notification) => !!notification.id)
    .map((notification) => ({
      id: notification.id as string,
      body: notification.attributes.body,
      link: notification.attributes.link,
      readAt: notification.attributes["read-at"],
      createdAt: notification.attributes["created-at"],
    }));

  const pageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  const columns: ColumnDef<NotificationRowData>[] = [
    {
      id: "notification",
      header: () => (
        <div className="notificationsColumnHeader">
          Notification
          <button
            type="button"
            className="notificationsClearAllButton"
            onClick={handleClearAll}
          >
            Clear All
          </button>
        </div>
      ),
      cell: (props) => (
        <NotificationRow
          id={props.row.original.id}
          body={props.row.original.body}
          link={props.row.original.link}
          readAt={props.row.original.readAt}
          createdAt={props.row.original.createdAt}
          onView={handleView}
          variant="table"
        />
      ),
    },
  ];

  return (
    <div className="notificationsWrapper">
      <Row>
        <Col>
          <h1 className="notificationsTitle">Notifications</h1>
        </Col>
        <hr className="notificationsLine" />
      </Row>
      <Row>
        <Col>
          {isLoading ? (
            <div className="notificationsStateCard">
              <ScopeStackSpinner />
            </div>
          ) : notifications.length === 0 ? (
            <div className="notificationsStateCard notificationsEmptyState">
              <h3>No notifications yet</h3>
            </div>
          ) : (
            <DataTable
              data={notifications}
              columns={columns}
              striped
              bordered
              totalPages={pageMeta.pageCount}
              totalRows={pageMeta.recordCount}
              currentPage={pageNumber}
              setCurrentPage={setPageNumber}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              paginationEnabled
              pageSizeEnabled
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default NotificationsMspa;
